import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';

export function useAdminAuth() {
    const [state, setState] = useState({ session: null as Session | null, isAdmin: false, assured: false, loading: true });
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        let active = true;
        let generation = 0;
        async function check(session: Session | null) {
            const current = ++generation;
            setState({ session, isAdmin: false, assured: false, loading: !!session });
            if (!session) return;
            try {
                const { data, error } = await supabase.from('platform_admins')
                    .select('id').eq('id', session.user.id).maybeSingle();
                const assurance = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
                if (active && current === generation) {
                    setState({ session, isAdmin: !error && !!data,
                        assured: !assurance.error && assurance.data?.currentLevel === 'aal2', loading: false });
                }
            } catch {
                if (active && current === generation) setState({ session, isAdmin: false, assured: false, loading: false });
            }
        }
        // Auth callbacks must not await another auth API while its lock is held.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            const eventGeneration = ++generation;
            setState({ session, isAdmin: false, assured: false, loading: !!session });
            setTimeout(() => { if (active && eventGeneration === generation) void check(session); }, 0);
        });
        const initial = generation;
        supabase.auth.getSession().then(({ data, error }) => {
            if (active && initial === generation) void check(error ? null : data.session);
        }).catch(() => {
            if (active && initial === generation) void check(null);
        });
        return () => { active = false; generation++; subscription.unsubscribe(); };
    }, [revision]);
    return { ...state, refresh: () => setRevision(value => value + 1) };
}
